/**
 * @fileoverview Reusable badge for inline labels with variant styling.
 *
 * @purpose Display badge-like statuses across admin UIs and shared cards.
 * @usage Imported by admin settings, Rag Manager, and shared components needing a badge.
 * @ownership Shared UI components (core)
 * @dependencies
 * - class-variance-authority (cva helper)
 * - react
 * - @/core/lib/utils (cn helper)
 * @restrictions
 * - DO NOT introduce layout-specific logic; it should stay inline/block.
 */
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from '../../lib/utils';

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        muted:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export type BadgeProps = {} & React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof badgeVariants>

function Badge({ className, variant, style, ...props }: BadgeProps) {
  const isPrimary = variant === 'default' || variant === undefined
  return (
    <div
      className={cn(badgeVariants({ variant }), className)}
      style={isPrimary ? { color: 'var(--primary-foreground)', ...style } : style}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
